import { useEffect, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Input, Select } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { settingsApi } from '../../api/settings.api'
import { toast } from '../../store/toastStore'
import { ApiError } from '../../api/client'

const TIMEZONES = ['Europe/Berlin', 'Europe/Vienna', 'Europe/Zurich', 'UTC']
const LOCALES = [
  { value: 'de', label: 'Deutsch' },
  { value: 'en', label: 'English' },
]

export function GeneralSettingsPanel() {
  const queryClient = useQueryClient()
  const settingsQuery = useQuery({ queryKey: ['settings', 'general'], queryFn: async () => (await settingsApi.get()).data })

  const [appName, setAppName] = useState('')
  const [timezone, setTimezone] = useState('Europe/Berlin')
  const [locale, setLocale] = useState('de')
  const [dateFormat, setDateFormat] = useState('DD.MM.YYYY HH:mm')
  const [logoUrl, setLogoUrl] = useState('')

  useEffect(() => {
    if (settingsQuery.data) {
      setAppName(settingsQuery.data.appName)
      setTimezone(settingsQuery.data.timezone)
      setLocale(settingsQuery.data.locale)
      setDateFormat(settingsQuery.data.dateFormat)
      setLogoUrl(settingsQuery.data.logoUrl)
    }
  }, [settingsQuery.data])

  const mutation = useMutation({
    mutationFn: () => settingsApi.update({ appName, timezone, locale, dateFormat, logoUrl }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      toast.success('Einstellungen gespeichert.')
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    mutation.mutate()
  }

  return (
    <Card style={{ maxWidth: 480 }}>
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <Field label="Panelname" htmlFor="appName">
          <Input id="appName" value={appName} onChange={(e) => setAppName(e.target.value)} required />
        </Field>
        <Field label="Zeitzone" htmlFor="timezone">
          <Select id="timezone" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Sprache" htmlFor="locale">
          <Select id="locale" value={locale} onChange={(e) => setLocale(e.target.value)}>
            {LOCALES.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Datumsformat" htmlFor="dateFormat" hint="z.B. DD.MM.YYYY HH:mm (24-Stunden-Format)">
          <Input id="dateFormat" value={dateFormat} onChange={(e) => setDateFormat(e.target.value)} required />
        </Field>
        <Field label="Logo" htmlFor="logoUrl" hint="Pfad ab / (z.B. /logo.png, unter frontend/public/ abgelegt) oder vollständige URL">
          <Input id="logoUrl" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="/logo.png" />
        </Field>
        <Button type="submit" variant="primary" loading={mutation.isPending} style={{ alignSelf: 'flex-start' }}>
          Speichern
        </Button>
      </form>
    </Card>
  )
}
